"use client"

import { graphql } from "@opensea/graphql"
import {
  OpenChestButton_treasureFragment,
  OpenChestButtonFragment,
} from "@/components/rewards/Rewards/TreasureChest/OpenChestButton.fragment"
import { TreasureChestWaveFragment } from "@/components/rewards/Rewards/TreasureChest/TreasureChestWave.fragment"
import { useAuthenticatedQuery } from "./use-authenticated-query"

const query = graphql(
  `
    query useOpenableTreasureChestsQuery {
      openableTreasureChests {
        items {
          id
          isOpenable
          userRewards {
            id
            claimedAt
          }
          userTokenRewards {
            id
            claimedAt
          }
          userProgress {
            totalPoints
            hasExceededLevelCap
            currentLevel
            currentTier
            progressPercent
          }
          userTreasures {
            ...OpenChestButton_treasure
          }
          ...TreasureChestWave
          ...OpenChestButton
        }
      }
      mostRecentlyEndedTreasureChest {
        id
        isOpenable
        userRewards {
          id
          claimedAt
        }
        userTokenRewards {
          id
          claimedAt
        }
        userProgress {
          totalPoints
          hasExceededLevelCap
          currentLevel
          currentTier
          progressPercent
        }
        userTreasures {
          ...OpenChestButton_treasure
        }
        ...TreasureChestWave
        ...OpenChestButton
      }
    }
  `,
  [
    TreasureChestWaveFragment,
    OpenChestButtonFragment,
    OpenChestButton_treasureFragment,
  ],
)

/**
 * Hook to fetch openable treasure chests for the authenticated user.
 * Returns both the openable chests list, the most recently ended chest, and treasures from chests.
 * Pauses query when user is not authenticated.
 */
export function useOpenableTreasureChests() {
  const [result, refetch] = useAuthenticatedQuery(
    {
      query,
      variables: {},
    },
    { throwOnError: false },
  )

  const { data, ...rest } = result

  const openableTreasureChests = data?.openableTreasureChests?.items ?? []
  const openableTreasureChest =
    data?.mostRecentlyEndedTreasureChest ?? openableTreasureChests.at(0)
  const mostRecentlyEndedTreasureChest = data?.mostRecentlyEndedTreasureChest

  const treasures = (openableTreasureChest?.userTreasures ?? []).filter(
    (treasure): treasure is NonNullable<typeof treasure> => Boolean(treasure),
  )

  return {
    openableTreasureChests,
    openableTreasureChest,
    mostRecentlyEndedTreasureChest,
    hasRewards:
      (openableTreasureChest?.userRewards?.length ?? 0) > 0 ||
      (openableTreasureChest?.userTokenRewards?.length ?? 0) > 0 ||
      treasures.length > 0,
    treasures,
    ...rest,
    refetch,
  }
}
